import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/betalning');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(email, password, name);
      navigate('/betalning');
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <a href="/" className="heading-font text-2xl font-medium text-[#0A0A0A]">Emotionell Frihet™</a>
        </div>
        
        <div className="bg-[#FEFCFA] rounded-2xl p-8 border border-[#E8E0DB]">
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#A0655A] text-white text-xs flex items-center justify-center font-medium">1</div>
              <span className="text-xs text-[#A0655A] font-medium">Konto</span>
            </div>
            <div className="w-8 h-px bg-[#EBE3DF]" />
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#EBE3DF] text-[#111111]/40 text-xs flex items-center justify-center font-medium">2</div>
              <span className="text-xs text-[#111111]/40">Betalning</span>
            </div>
            <div className="w-8 h-px bg-[#EBE3DF]" />
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#EBE3DF] text-[#111111]/40 text-xs flex items-center justify-center font-medium">3</div>
              <span className="text-xs text-[#111111]/40">Börja</span>
            </div>
          </div>

          <h1 className="heading-font text-2xl font-medium text-[#0A0A0A] mb-2 text-center">Skapa konto</h1>
          <p className="text-sm text-[#555] text-center mb-6">Skapa ditt konto först, sedan betalar du och får direkt tillgång.</p>
          
          {error && <p className="text-red-600 text-sm mb-4 text-center">{error}</p>}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="text"
              placeholder="Ditt namn"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="rounded-xl"
            />
            <Input
              type="email"
              placeholder="E-post"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="rounded-xl"
            />
            <Input
              type="password"
              placeholder="Lösenord (minst 6 tecken)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="rounded-xl"
            />
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full py-6"
            >
              {loading ? 'Skapar konto...' : 'Skapa konto'}
            </Button>
          </form>
          
          <p className="text-center text-sm text-[#111111] mt-6">
            Har du redan ett konto? <a href="/logga-in" className="text-[#A0655A] hover:underline">Logga in</a>
          </p>
        </div>

        <div className="text-center mt-6">
          <a href="/" className="text-sm text-[#A0655A] hover:underline flex items-center justify-center gap-1">
            <ChevronLeft size={16} /> Tillbaka till startsidan
          </a>
        </div>
      </div>
    </div>
  );
}
